import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

function FocusGroupAnalyticsSummary({ filterClassCode = 'all' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await axios.get('/api/focus-group/analytics', { withCredentials: true });
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading || !data) return null;

  const group = filterClassCode !== 'all' && data.engagementScores[filterClassCode]
    ? filterClassCode : 'all';

  const engagementAvg = data.engagementScores[group]?.avg || 0;

  // Calculate average rating across all rating questions
  const questions = data.questionAggregates[group] || {};
  const ratingQuestions = Object.values(questions).filter(q => q.type === 'rating' && q.avg);
  const avgRating = ratingQuestions.length > 0
    ? Math.round((ratingQuestions.reduce((s, q) => s + q.avg, 0) / ratingQuestions.length) * 10) / 10
    : 0;

  // Find top mission from multiple choice questions about missions
  const missionQ = Object.entries(questions).find(([k]) =>
    k.toLowerCase().includes('most helpful mission') || k.toLowerCase().includes('most valuable mission')
  );
  let topMission = '-';
  if (missionQ && missionQ[1].frequencies) {
    const sorted = Object.entries(missionQ[1].frequencies).sort((a, b) => b[1] - a[1]);
    if (sorted.length > 0) topMission = sorted[0][0];
  }

  // Future use percentage
  const futureQ = Object.entries(questions).find(([k]) =>
    k.toLowerCase().includes('continue using') || k.toLowerCase().includes('future use') || k.toLowerCase().includes('recommend')
  );
  let futureUse = null;
  if (futureQ && futureQ[1].frequencies) {
    const freqs = futureQ[1].frequencies;
    const total = Object.values(freqs).reduce((s, v) => s + v, 0);
    const positive = Object.entries(freqs)
      .filter(([k]) => k.toLowerCase().includes('yes') || k.toLowerCase().includes('maybe') || k.toLowerCase().includes('definitely'))
      .reduce((s, [, v]) => s + v, 0);
    futureUse = total > 0 ? Math.round((positive / total) * 100) : null;
  }

  const engagementColor = engagementAvg > 70 ? 'text-green-600' : engagementAvg > 40 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg shadow p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Analytics Snapshot</h3>
        <Link
          to="/focus-groups/analytics"
          className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
        >
          View Full Analytics →
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-3 text-center">
          <div className={`text-2xl font-bold ${engagementColor}`}>{engagementAvg}</div>
          <div className="text-xs text-gray-500">Avg Engagement</div>
        </div>
        <div className="bg-white rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-indigo-600">{avgRating}</div>
          <div className="text-xs text-gray-500">Avg Rating</div>
          {avgRating > 0 && (
            <div className="text-amber-400 text-xs">
              {'★'.repeat(Math.round(avgRating))}{'☆'.repeat(Math.max(0, 5 - Math.round(avgRating)))}
            </div>
          )}
        </div>
        <div className="bg-white rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-purple-600 truncate" title={topMission}>{topMission}</div>
          <div className="text-xs text-gray-500">Top Mission</div>
        </div>
        <div className="bg-white rounded-lg p-3 text-center">
          {futureUse !== null ? (
            <>
              <div className="text-2xl font-bold text-green-600">{futureUse}%</div>
              <div className="text-xs text-gray-500">Would Continue</div>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-gray-400">-</div>
              <div className="text-xs text-gray-500">Future Use</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default FocusGroupAnalyticsSummary;
